<!--
  悬浮客服二维码（业务页常驻）。

  形态：
    - 展开态：右下角二维码卡片（官方客服 + 提示文案），右上角「—」收起；
    - 收起态：竖排「联系客服」小框，点击重新展开。
  收起状态写入 localStorage：跳转其它业务页 / 刷新页面都保持小框，
  只有用户主动点击小框才会重新展开。

  不影响业务操作的保证：
    1) 不使用 modal / 遮罩，永不铺满全屏；
    2) 定位容器 pointer-events:none，只有卡片与小框本身可点
       —— 卡片以外的区域完全不拦截页面上的鼠标操作；
    3) 不锁滚动、不改 body 样式、不监听全局键盘事件；
    4) z-index 1500：高于页面常驻元素（TopBar 999 / Tooltip 1000 / 评分表 1000），
       低于 Element 弹窗（2000+），打开对话框时会被正常盖住，不干扰弹窗。

  挂载点：src/App.vue（App 级唯一挂载 → 路由切换不重新挂载，收起状态得以保持），
  按路由前缀显隐，详见 App.vue 的 showCustomerServiceFloat。
-->
<template>
  <div class="cs-float">
    <!-- 展开态：官方客服二维码卡片 -->
    <div v-if="!collapsed" class="cs-card">
      <button class="cs-card__close" type="button" title="收起" @click="collapse">—</button>
      <p class="cs-card__title">{{ title }}</p>
      <img class="cs-card__qr" :src="qrImage" alt="官方客服二维码" />
      <p class="cs-card__tip">{{ tip }}</p>
      <p class="cs-card__sub">微信扫一扫，添加客服</p>
    </div>

    <!-- 收起态：竖排「联系客服」小框，点击重新展开 -->
    <button v-else class="cs-mini" type="button" :title="miniText" @click="expand">
      <span class="cs-mini__text">{{ miniText }}</span>
    </button>
  </div>
</template>

<script>
// 复用现有客服二维码（与站内「联系客服」9 个页面同一张）
import wechartCode from '../assets/images/wechartCode.jpg'

/** 收起状态记忆键（localStorage）：'1' = 小框，其它/缺省 = 展开 */
const COLLAPSED_STORAGE_KEY = 'customerServiceFloat.collapsed'

/** 读取上次的收起状态；本地存储不可用时默认展开 */
function readCollapsedState () {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false
    }
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
  } catch (error) {
    return false
  }
}

export default {
  name: 'CustomerServiceFloat',
  data () {
    return {
      // 初始值直接读存储：避免"先展开再收起"的闪动
      collapsed: readCollapsedState(),
      title: '星雲鑑官方客服',
      tip: '如需咨询或协助，请扫码添加官方客服微信，我们会尽快为您服务',
      miniText: '联系客服',
      qrImage: wechartCode
    }
  },
  methods: {
    /** 收起成小框（只改形态，不销毁，用户随时可点回来） */
    collapse () {
      this.collapsed = true
      this.persistCollapsed(true)
    },
    /** 从小框重新展开二维码卡片 */
    expand () {
      this.collapsed = false
      this.persistCollapsed(false)
    },
    /** 记忆收起状态：跳转其它业务页 / 刷新后保持同一形态 */
    persistCollapsed (collapsed) {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0')
        }
      } catch (error) {
        // 存储不可用时仅失去"记忆"能力，不影响弹窗本身的展开/收起
      }
    }
  }
}
</script>

<style scoped>
.cs-float {
  position: fixed;
  right: 24px;
  bottom: 24px;
  /* 关键：定位容器不吃事件，只有卡片 / 小框自身可点 */
  pointer-events: none;
  /* 高于页面常驻元素，低于 Element 弹窗（2000+） */
  z-index: 1500;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

/* ---------------- 展开态：二维码卡片 ---------------- */
.cs-card {
  pointer-events: auto;
  position: relative;
  width: 200px;
  padding: 16px 14px 12px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  text-align: center;
  animation: cs-card-in 0.18s ease-out;
}

@keyframes cs-card-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.cs-card__close {
  position: absolute;
  top: 6px;
  right: 8px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f2f4f7;
  color: #6b7280;
  font-size: 14px;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.cs-card__close:hover {
  background: #e4e8ee;
  color: #2c3e50;
}

.cs-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.cs-card__qr {
  display: block;
  width: 100%;
  margin: 10px 0 8px;
  border-radius: 6px;
}

.cs-card__tip {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #555;
  text-align: left;
}

.cs-card__sub {
  margin: 6px 0 0;
  font-size: 11px;
  color: #9aa0a6;
}

/* ---------------- 收起态：竖排「联系客服」小框 ---------------- */
.cs-mini {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 12px 0;
  border: none;
  border-radius: 8px;
  background: #3498db;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease, background 0.15s ease;
}

.cs-mini:hover {
  background: #2f8bcc;
  transform: translateY(-2px);
}

.cs-mini__text {
  /* 竖排：联系客服 自上而下排列 */
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 3px;
  font-size: 13px;
  line-height: 1;
}

/* ---------------- 移动端：贴边收窄，避免遮挡底部操作 ---------------- */
@media (max-width: 768px) {
  .cs-float {
    right: 12px;
    bottom: 12px;
  }

  .cs-card {
    width: 168px;
    padding: 14px 12px 10px;
  }

  .cs-card__tip {
    font-size: 11px;
    line-height: 16px;
  }
}
</style>
